<template>
  <!-- Disbursements — cloned from the settlement prototype (v8) -->
  <div class="disb-wrap" data-cy="settlement-disbursement-table">
    <div class="disb-header">
      <span class="disb-title">Disbursements</span>
      <div class="disb-header-actions">
        <LlSecondaryButton
          color="primary"
          variant="outlined"
          size="small"
          class="disb-btn"
          data-cy="disb-bulk-submit"
          :loading="bulkBusy"
          :disabled="!canBulkSubmit"
          @click="submitSelected"
        >
          Submit to QuickBooks
        </LlSecondaryButton>
        <LlSecondaryButton
          v-if="anySubmitted"
          :color="AMBER"
          variant="flat"
          size="small"
          class="disb-btn text-white"
          data-cy="disb-bulk-undo"
          :disabled="!canBulkUndo"
          title="Revert submitted disbursements"
          @click="undoSelected"
        >
          Undo
        </LlSecondaryButton>
      </div>
    </div>

    <div v-if="loading && !rows.length" class="disb-state" data-cy="disb-loading">
      <v-progress-circular indeterminate color="success" size="28" />
    </div>

    <div
      v-else-if="loadError && !rows.length"
      class="disb-state disb-state--error"
      data-cy="disb-load-error"
    >
      <div class="mb-2">{{ loadError }}</div>
      <LlSecondaryButton
        color="primary"
        variant="outlined"
        size="small"
        class="disb-btn-sm"
        data-cy="disb-reload"
        @click="reload"
      >
        Retry
      </LlSecondaryButton>
    </div>

    <template v-else>
      <!-- ═══ Client disbursement — lifted out of the bulk table (sc-46626) ═══
           Paid through Send, never the bulk submit, so it carries no checkbox. -->
      <!-- Suppressed entirely when net recovery is <= 0: there is no row to head, and the
           panel already shows its own negative-net warning above this table. -->
      <template v-if="payabliEnabled && clientRow">
        <div class="flex items-center gap-1 text-[11px] font-bold tracking-[0.04em] uppercase text-[#747474] mb-2" data-cy="disb-client-heading">
          Client Disbursement
          <LlTooltip location="top">
            <div class="max-w-[280px]">
              Your client's net recovery, paid by secure payment link. Funds leave
              trust when the client enters their bank details.
            </div>
            <template #activator>
              <span class="info-dot" tabindex="0" data-cy="disb-client-info">i</span>
            </template>
          </LlTooltip>
        </div>

        <div class="border border-[#e5e5e5] rounded-md px-[14px] py-3 text-[13px]" data-cy="disb-client-section">
          <!-- Trust callout: two numbers, shown while a Send is still possible. It is the
               pre-flight for an action that has not happened yet, so it goes once one has.
               data-shortfall is the test hook — kept separate from the Tailwind classes below so
               a future styling tweak can't silently break what the shortfall test asserts on. -->
          <div
            v-if="showTrustCallout"
            class="border rounded px-[10px] py-2 mb-3"
            :class="trustShortfall ? 'border-[rgb(var(--v-theme-error))] bg-[rgba(var(--v-theme-error),0.08)]' : 'border-[#e5e5e5] bg-[#fafafa]'"
            :data-shortfall="trustShortfall || undefined"
            data-cy="disb-trust-callout"
          >
            <div class="flex flex-wrap gap-[18px] text-xs text-[#747474]">
              <span>
                Trust balance:
                <strong data-cy="disb-trust-balance">
                  {{ trustBalance === null ? '—' : formatCurrency(trustBalance) }}
                </strong>
              </span>
              <span>
                Disbursing:
                <strong data-cy="disb-trust-amount">{{ formatCurrency(clientRow.amount) }}</strong>
              </span>
            </div>
            <!-- Always danger: this only renders when Send is blocked, and every cause is a hard
                 block. The callout's own red tint above stays reserved for the shortfall (UI-003),
                 where the two figures are themselves the problem — colouring this for a missing
                 connection would point at the wrong thing. -->
            <div
              v-if="clientRow.sendBlockedReason"
              class="mt-[6px] text-xs text-error"
              data-cy="disb-send-blocked-reason"
            >
              {{ clientRow.sendBlockedReason }}
            </div>
          </div>

          <div class="flex items-center gap-4">
            <div class="flex-[1_1_auto] min-w-0">
              <div class="disb-recipient">{{ clientRow.recipientName }}</div>
              <div class="disb-kind">{{ clientRow.recipientKind }}</div>
            </div>
            <div class="flex-[0_1_190px] disb-type">{{ clientRow.typeLabel }}</div>
            <div class="flex-none ml-auto disb-amount">{{ formatCurrency(clientRow.amount) }}</div>
            <div class="flex items-center justify-end gap-1 flex-[0_0_250px]">
              <v-progress-circular
                v-if="clientRow.state === 'in-progress'"
                indeterminate
                color="success"
                size="18"
                width="2"
                data-cy="disb-spinner-client"
              />

              <!-- Nothing sent yet — the only state offering an action to start one. -->
              <template v-else-if="payabliState === 'notSent'">
                <LlSecondaryButton
                  color="primary"
                  variant="outlined"
                  size="small"
                  class="disb-btn-sm"
                  :disabled="anyBusy || !clientRow.canSend || !sendDialogContext"
                  data-cy="disb-send-client"
                  @click="openSend"
                >
                  Send
                </LlSecondaryButton>
                <LlMenu
                  location="bottom end"
                  :items="[{
                    title: 'Record Manual Disbursement',
                    dataCy: 'disb-record-manual',
                    disabled: anyBusy,
                    action: recordManualDisbursement,
                  }]"
                >
                  <template #activator="{ props: menuProps }">
                    <LlSecondaryButton
                      v-bind="menuProps"
                      icon="mdi-dots-vertical"
                      variant="text"
                      size="small"
                      density="comfortable"
                      aria-label="More client disbursement actions"
                      data-cy="disb-client-menu"
                    />
                  </template>
                </LlMenu>
              </template>

              <!-- Every other state is a status chip with its action beside it, always visible —
                   the pairing MatterFeeRecipientsTab uses for its disbursement rows. A withheld
                   action stays on the row, disabled, with its reason on hover. data-outcome is a
                   test hook, kept separate from the Tailwind classes. -->
              <span
                v-else
                class="inline-flex items-center justify-end gap-2"
                :data-outcome="payabliState === 'disbursed' ? 'success' : payabliState === 'returned' ? 'failure' : undefined"
                :data-cy="`disb-client-state-${payabliState}`"
              >
                <LlTooltip location="top" :text="payabliStateTooltip ?? undefined">
                  <template #activator>
                    <span
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap"
                      :class="payabliChip.classes"
                      :tabindex="payabliStateTooltip ? 0 : undefined"
                      data-cy="disb-client-chip"
                    >
                      <LlIcon :icon="payabliChip.icon" size="12" />
                      {{ payabliStateLabel }}
                    </span>
                  </template>
                </LlTooltip>

                <LlSecondaryButton
                  v-if="hasPayabliAction('cancel') && payabliState === 'cancelPending'"
                  :color="AMBER"
                  variant="flat"
                  size="small"
                  class="disb-btn-sm text-white"
                  :disabled="anyBusy"
                  data-cy="disb-cancel-client"
                  @click="cancelClientDisbursement"
                ><QboIcon size="14" class="mr-1" />Retry QuickBooks Sync</LlSecondaryButton>
                <LlSecondaryButton
                  v-else-if="hasPayabliAction('cancel')"
                  :color="AMBER"
                  variant="text"
                  size="small"
                  class="disb-btn-sm"
                  :disabled="anyBusy"
                  data-cy="disb-cancel-client"
                  @click="cancelClientDisbursement"
                >Cancel</LlSecondaryButton>
                <LlTooltip v-else-if="clientRow.payabli?.cancelBlockedReason" location="top">
                  <template #default>{{ clientRow.payabli.cancelBlockedReason }}</template>
                  <template #activator>
                    <span data-cy="disb-cancel-client-withheld">
                      <LlSecondaryButton :color="AMBER" variant="text" size="small" class="disb-btn-sm" disabled>Cancel</LlSecondaryButton>
                    </span>
                  </template>
                </LlTooltip>

                <LlSecondaryButton
                  v-if="hasPayabliAction('resendEmail')"
                  color="primary"
                  variant="outlined"
                  size="small"
                  class="disb-btn-sm"
                  :disabled="anyBusy || resendLoading"
                  :loading="resendLoading"
                  data-cy="disb-resend-email-client"
                  @click="openResend"
                >Resend Email</LlSecondaryButton>

                <LlSecondaryButton
                  v-if="hasPayabliAction('undo')"
                  :color="AMBER"
                  variant="text"
                  size="small"
                  class="disb-btn-sm"
                  :disabled="anyBusy"
                  data-cy="disb-undo-client"
                  @click="undoRow(clientRow)"
                >Undo</LlSecondaryButton>
                <LlTooltip
                  v-else-if="payabliState === 'submittedManual' && clientRow.undoBlockedReason"
                  location="top"
                >
                  <template #default>{{ clientRow.undoBlockedReason }}</template>
                  <template #activator>
                    <span data-cy="disb-undo-client-withheld">
                      <LlSecondaryButton :color="AMBER" variant="text" size="small" class="disb-btn-sm" disabled>Undo</LlSecondaryButton>
                    </span>
                  </template>
                </LlTooltip>
              </span>
            </div>
          </div>

          <div v-if="clientBlockedReason" class="mt-2">
            <span
              class="disb-msg"
              :class="{ 'disb-msg--error': clientRow.state === 'error' }"
              data-cy="disb-message-client"
            >
              {{ clientBlockedReason }}
            </span>
          </div>
        </div>

        <div class="flex items-center gap-1 text-[11px] font-bold tracking-[0.04em] uppercase text-[#747474] mb-2 mt-[22px]" data-cy="disb-other-heading">
          Other Disbursements
          <LlTooltip location="top">
            <div class="max-w-[280px]">
              Payees paid directly from trust through QuickBooks — the firm's invoice,
              lien holders and co-counsel. Select rows to submit them together.
            </div>
            <template #activator>
              <span class="info-dot" tabindex="0" data-cy="disb-other-info">i</span>
            </template>
          </LlTooltip>
        </div>
      </template>

      <table class="disb-table" data-cy="disb-table">
      <thead>
        <tr>
          <th class="disb-col-check">
            <LlCheckbox
              :model-value="allSelected"
              :indeterminate="someSelected"
              aria-label="Select all disbursements"
              data-cy="disb-select-all"
              :disabled="!rows.length || !hasActionableRows"
              dense
              class="d-inline-flex"
              @update:model-value="toggleAll"
            />
          </th>
          <th class="disb-th text-left">Recipient</th>
          <th class="disb-th text-left">Type</th>
          <th class="disb-th text-right">Amount</th>
          <th class="disb-col-action"></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="row in rows" :key="row.key">
          <tr
            class="disb-row"
            :class="{
              'disb-row--firm': row.key === 'firm',
              'disb-row--submitted': row.state === 'done',
            }"
            :data-cy="`disb-row-${row.key}`"
          >
            <td class="disb-col-check">
              <LlCheckbox
                :model-value="isSelected(row.key)"
                :aria-label="`Select ${row.recipientName}`"
                :data-cy="`disb-check-${row.key}`"
                :disabled="!isCheckable(row)"
                dense
                class="d-inline-flex"
                @update:model-value="(checked: boolean) => toggleRow(row.key, checked)"
              />
            </td>
            <td>
              <div class="disb-recipient">{{ row.recipientName }}</div>
              <div class="disb-kind">{{ row.recipientKind }}</div>
            </td>
            <td class="disb-type">
              {{ row.typeLabel }}
              <LlTooltip v-if="row.feeAmount !== null" location="top">
                <div style="max-width: 260px">
                  Attorney fees round down to the nearest cent; any leftover goes to
                  your firm's share so the client is never overcharged.
                </div>
                <template #activator>
                  <span class="info-dot" tabindex="0" data-cy="disb-fee-rounding-info">i</span>
                </template>
              </LlTooltip>
              <div v-if="row.feeAmount !== null" class="disb-breakdown">
                <template v-if="row.expenseAmount && row.expenseAmount > 0">
                  {{ formatCurrency(row.feeAmount) }} fee + {{ formatCurrency(row.expenseAmount) }} expenses
                </template>
                <template v-else>{{ row.percentage?.toFixed(4) }}% of total fee</template>
              </div>
              <LlTooltip v-if="row.key === 'firm'" location="top">
                <div style="max-width: 260px">
                  The firm's invoice (attorney fee + expenses) is finalized and
                  paid from trust automatically when you submit this to QuickBooks.
                </div>
                <template #activator>
                  <span class="info-dot" tabindex="0">i</span>
                </template>
              </LlTooltip>
            </td>
            <td class="disb-amount">{{ formatCurrency(row.amount) }}</td>
            <td class="disb-status">
              <!-- In progress -->
              <v-progress-circular
                v-if="row.state === 'in-progress'"
                indeterminate
                color="success"
                size="18"
                width="2"
                :data-cy="`disb-spinner-${row.key}`"
              />

              <!-- Submitted — green label; hovering the cell reveals the Undo button.
                   When the row can't be reversed yet (e.g. the firm invoice while the
                   client net is still pushed) the Undo is shown DISABLED with a tooltip
                   explaining why, rather than hidden or silently failing. -->
              <span v-else-if="row.state === 'done'" class="disb-submitted">
                <span class="disb-submitted-label">Submitted</span>
                <LlSecondaryButton
                  v-if="row.canUndo"
                  :color="AMBER"
                  variant="flat"
                  size="small"
                  class="disb-btn-sm text-white disb-undo-btn"
                  :disabled="anyBusy"
                  :data-cy="`disb-undo-${row.key}`"
                  @click="undoRow(row)"
                >
                  Undo
                </LlSecondaryButton>
                <LlTooltip v-else location="top">
                  <div style="max-width: 260px">
                    {{ row.undoBlockedReason || undoBlockedReasonFallback(row.key) }}
                  </div>
                  <template #activator>
                    <span class="disb-undo-btn">
                      <LlSecondaryButton
                        :color="AMBER"
                        variant="flat"
                        size="small"
                        class="disb-btn-sm text-white"
                        disabled
                        :data-cy="`disb-undo-${row.key}`"
                      >
                        Undo
                      </LlSecondaryButton>
                    </span>
                  </template>
                </LlTooltip>
              </span>

              <!-- Error / saga error — Retry and/or Undo. A needs-recovery firm
                   invoice (invoiced but not paid) is typically both retryable and
                   undoable, so both actions can appear together — unlike the
                   'done' state above, they aren't mutually exclusive here. -->
              <span
                v-else-if="row.state === 'error' || row.state === 'needs-recovery'"
                class="disb-recovery-actions"
              >
                <LlSecondaryButton
                  v-if="row.canSubmit"
                  :color="AMBER"
                  variant="flat"
                  size="small"
                  class="disb-btn-sm text-white"
                  :disabled="anyBusy"
                  :data-cy="`disb-retry-${row.key}`"
                  @click="submitRow(row)"
                >
                  Retry
                </LlSecondaryButton>
                <LlSecondaryButton
                  v-if="row.canUndo"
                  :color="AMBER"
                  variant="flat"
                  size="small"
                  class="disb-btn-sm text-white"
                  :disabled="anyBusy"
                  :data-cy="`disb-undo-${row.key}`"
                  @click="undoRow(row)"
                >
                  Undo
                </LlSecondaryButton>
              </span>

              <!-- Pending — Submit -->
              <LlSecondaryButton
                v-else-if="row.state === 'pending'"
                color="primary"
                variant="outlined"
                size="small"
                class="disb-btn-sm"
                :disabled="anyBusy"
                :data-cy="`disb-submit-${row.key}`"
                @click="submitRow(row)"
              >
                Submit
              </LlSecondaryButton>

              <!-- Blocked / no action available -->
              <span v-else class="disb-dash" :data-cy="`disb-blocked-${row.key}`">
                —
              </span>
            </td>
          </tr>
          <!-- Inline message: submit error, needs-recovery, failed-undo reason, or blocked reason -->
          <tr v-if="row.message" class="disb-msg-row">
            <td></td>
            <td colspan="4">
              <span
                class="disb-msg"
                :class="{
                  'disb-msg--error':
                    row.state === 'error' ||
                    row.state === 'needs-recovery' ||
                    (row.state === 'done' && !!row.message),
                }"
                :data-cy="`disb-message-${row.key}`"
              >
                {{ row.message }}
              </span>
            </td>
          </tr>
        </template>

        <tr v-if="!rows.length && !loading">
          <td colspan="5" class="disb-empty">No disbursements to display.</td>
        </tr>
      </tbody>
      </table>
    </template>

    <LlConfirmationDialog
      ref="undoDialog"
      title="Reverse QuickBooks Payment?"
      :message="undoMessage"
      confirm-text="Reverse Payment"
      show-warning-icon
      data-cy="disb-undo-dialog"
    />
    <LlConfirmationDialog
      ref="manualDialog"
      title="Record Manual Disbursement?"
      :message="manualMessage"
      confirm-text="Record Disbursement"
      show-warning-icon
      data-cy="disb-manual-dialog"
    />
    <LlConfirmationDialog
      ref="cancelDialog"
      :title="cancelTitle"
      :message="cancelMessage"
      :confirm-text="cancelConfirmText"
      show-warning-icon
      data-cy="disb-cancel-dialog"
    />
    <SendDepositRequestDialog
      v-if="sendDialogContext"
      v-model="sendDialogOpen"
      v-bind="sendDialogContext"
      :resend-from="resendFrom"
      :sending="clientSending"
      :error-message="clientError"
      @submit="onSendSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type {
  DisbursementEmailComposition,
  PayabliRowAction,
  PayabliRowState,
  SendClientDisbursementPayload,
} from '@/models/disbursementTypes';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlMenu from '@/components/LlMenu.vue';
import QboIcon from '@/components/QboIcon.vue';
import LlIcon from '@/components/LlIcon.vue';
import { formatCurrency } from '@/services/utils';
import { undoBlockedReasonFallback } from '@/utils/disbursementHelpers';
import SendDepositRequestDialog from './SendDepositRequestDialog.vue';
import { useSettlementStore } from '../stores/useSettlementStore';
import { useDisbursements } from '../composables/useDisbursements';

// Brand color for the amber (warning) action buttons. Centralized here — and as a
// CSS var in the scoped styles — so a future Vuetify theme-token migration has one
// place to change per concern.
const AMBER = '#f59e0b';

const undoDialog = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);
const undoMessage = ref('');

/** Bridges the composable's confirm flow to the dialog (a UI concern owned here). */
async function confirmUndo(recipientLabel: string): Promise<boolean> {
  undoMessage.value = `This will reverse the QuickBooks payment for ${recipientLabel}. Continue?`;
  if (!undoDialog.value) return false;
  return undoDialog.value.open();
}

const manualDialog = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);
const manualMessage = ref('');

async function confirmRecordManual(recipientLabel: string): Promise<boolean> {
  manualMessage.value =
    `This will write a QuickBooks check for ${recipientLabel} and record the disbursement as `
    + 'already paid outside Payabli. Continue?';
  if (!manualDialog.value) return false;
  return manualDialog.value.open();
}

const cancelDialog = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);
const cancelTitle = ref('');
const cancelMessage = ref('');
const cancelConfirmText = ref('');

// A retry is a different action from the payee's point of view: their link is already dead, and
// only the QuickBooks check is left to reverse.
async function confirmCancel(recipientLabel: string, amount: number): Promise<boolean> {
  if (payabliState.value === 'cancelPending') {
    cancelTitle.value = 'Retry QuickBooks Sync?';
    cancelMessage.value =
      `The ${formatCurrency(amount)} payment link to ${recipientLabel} is already canceled. `
      + 'This retries reversing its QuickBooks check. Continue?';
    cancelConfirmText.value = 'Retry QuickBooks Sync';
  } else {
    cancelTitle.value = 'Cancel Payment Link?';
    cancelMessage.value =
      `This will cancel the ${formatCurrency(amount)} payment link sent to ${recipientLabel}. `
      + "They will no longer be able to use it, and this can't be undone. Continue?";
    cancelConfirmText.value = 'Cancel Link';
  }
  if (!cancelDialog.value) return false;
  return cancelDialog.value.open();
}

const {
  rows,
  loading,
  loadError,
  anySubmitted,
  anyBusy,
  bulkBusy,
  // payabliConnected is deliberately not read here: the backend already folds VR-007 into
  // sendBlockedReason and withdraws the cancel action, so re-deriving it would be a second
  // authority on the same rule.
  payabliEnabled,
  trustBalance,
  clientRow,
  canBulkSubmit,
  canBulkUndo,
  isSelected,
  isCheckable,
  hasActionableRows,
  allSelected,
  someSelected,
  toggleRow,
  toggleAll,
  submitRow,
  undoRow,
  submitSelected,
  undoSelected,
  recordManualDisbursement,
  sendClientDisbursement,
  resendClientDisbursementEmail,
  loadClientDisbursementEmail,
  cancelClientDisbursement,
  clientSending,
  clientError,
  reload,
} = useDisbursements({ confirmUndo, confirmRecordManual, confirmCancel });

const settlementStore = useSettlementStore();
const { handleError } = useSnackbarHandler();
const sendDialogOpen = ref(false);
/** Set only while the dialog is open as a resend; null means an ordinary Send. */
const resendFrom = ref<DisbursementEmailComposition | null>(null);
const resendLoading = ref(false);

/** Null until the settlement and its client row are both loaded — the dialog needs all of it. */
const sendDialogContext = computed(() => {
  const settlement = settlementStore.data;
  const row = clientRow.value;
  if (!settlement?.matterId || !row) return null;
  return {
    matterId: settlement.matterId,
    clientName: row.recipientName,
    // A resend is for the amount already sent, not the live net recovery.
    amount: resendFrom.value ? row.payabli?.amount ?? row.amount : row.amount,
  };
});

function openSend(): void {
  resendFrom.value = null;
  sendDialogOpen.value = true;
}

async function openResend(): Promise<void> {
  if (resendLoading.value) return;
  resendLoading.value = true;
  try {
    const previous = await loadClientDisbursementEmail();
    if (!previous) return;
    resendFrom.value = previous;
    sendDialogOpen.value = true;
  } catch (error) {
    handleError(error);
  } finally {
    resendLoading.value = false;
  }
}

async function onSendSubmit(payload: SendClientDisbursementPayload): Promise<void> {
  try {
    // False means the call was suppressed as a duplicate — nothing was posted, so the modal
    // stays open rather than reading as a send that went out.
    const sent = resendFrom.value
      ? await resendClientDisbursementEmail(payload)
      : await sendClientDisbursement(payload);
    if (sent) sendDialogOpen.value = false;
  } catch {
    // The store already recorded the reason against the client row, which the modal renders.
  }
}

// The dialog only mounts once the settlement resolves. Without this, a click made before then
// would leave the flag set and the modal would spring open on its own when the data arrived.
watch(sendDialogContext, (context) => {
  if (!context) sendDialogOpen.value = false;
});

const PAYABLI_STATE_LABELS: Record<PayabliRowState, string> = {
  notSent: '',
  sent: 'Sent',
  inTransit: 'Funds in Transit',
  submittedManual: 'Submitted',
  disbursed: 'Disbursed',
  cancelPending: 'Cancel Pending',
  returned: 'Returned',
};

const payabliState = computed<PayabliRowState | null>(
  () => clientRow.value?.payabli?.state ?? null,
);
const emailDeliveryPending = computed(() => !!clientRow.value?.payabli?.emailDeliveryPending);

const payabliStateLabel = computed(() => {
  if (emailDeliveryPending.value) return 'Email Not Sent';
  return payabliState.value ? PAYABLI_STATE_LABELS[payabliState.value] : '';
});

/** The backend decides eligibility; the component only renders what it was given. */
const hasPayabliAction = (action: PayabliRowAction): boolean =>
  !!clientRow.value?.payabli?.actions.includes(action);

// Chip colours and icons are MatterFeeRecipientsTab's, so a disbursement reads the same on both
// screens; blue marks a manual disbursement (frd.md UI-019).
const PAYABLI_CHIPS: Record<PayabliRowState, { classes: string; icon: string }> = {
  notSent: { classes: 'bg-[#f1f5f9] text-[#475569] border-[#e2e8f0]', icon: 'mdi-clock-outline' },
  sent: { classes: 'bg-[#f1f5f9] text-[#475569] border-[#e2e8f0]', icon: 'mdi-email-outline' },
  inTransit: { classes: 'bg-[#fef3c7] text-[#92400e] border-[#fde68a]', icon: 'mdi-clock-outline' },
  submittedManual: { classes: 'bg-[#dbeafe] text-[#1e40af] border-[#bfdbfe]', icon: 'mdi-check-circle' },
  disbursed: { classes: 'bg-[#dcfce7] text-[#166534] border-[#bbf7d0]', icon: 'mdi-check-circle' },
  cancelPending: { classes: 'bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]', icon: 'mdi-alert-circle' },
  returned: { classes: 'bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]', icon: 'mdi-alert-circle' },
};

const PAYABLI_STATE_TOOLTIPS: Partial<Record<PayabliRowState, string>> = {
  sent: 'An email went to the client with a secure link to enter their bank details and request the deposit. Nothing has moved yet.',
  inTransit: 'The client entered their bank details and requested the deposit. Funds are on the way to their account.',
  disbursed: "The deposit has landed in the client's bank account. This disbursement is complete.",
};

const EMAIL_NOT_SENT_CHIP = { classes: 'bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]', icon: 'mdi-email-alert-outline' };
const EMAIL_NOT_SENT_TOOLTIP =
  "The payment link and QuickBooks check are in place, but the email to the client didn't go out, "
  + "so they may not have the link. Resending doesn't create a new link or touch QuickBooks.";

const payabliChip = computed(() =>
  (emailDeliveryPending.value ? EMAIL_NOT_SENT_CHIP : PAYABLI_CHIPS[payabliState.value ?? 'notSent']),
);
const payabliStateTooltip = computed(() => {
  if (emailDeliveryPending.value) return EMAIL_NOT_SENT_TOOLTIP;
  return payabliState.value ? PAYABLI_STATE_TOOLTIPS[payabliState.value] ?? null : null;
});

// Two numbers before a send is possible. Dropped once one has happened: the check is a pre-flight
// for an action no longer on offer, and it keeps evaluating against a balance the send already
// debited.
const showTrustCallout = computed(() => payabliState.value === 'notSent');
const trustShortfall = computed(
  () =>
    !!clientRow.value &&
    (trustBalance.value === null || clientRow.value.amount > trustBalance.value),
);

// sendBlockedReason is rendered inside the callout, so repeating it under the row would show the
// same sentence twice. A transient failure still has to surface: clientRow.state (the busy/error
// overlay) and payabliState (the backend's discriminator) are different axes, so a failed manual
// disbursement leaves state 'error' while payabliState is still 'notSent'.
const clientBlockedReason = computed(() => {
  const row = clientRow.value;
  if (!row) return null;
  if (row.state === 'error') return row.message;
  return showTrustCallout.value ? null : row.message ?? null;
});
</script>

<style scoped>
/* Cloned from the settlement prototype (v8). Palette: brand green var(--ll-green),
   text #424242, muted #747474/#8a929c, borders #E5E5E5/#f5f5f5. */
.disb-wrap {
  /* Brand/semantic colors in one place (swap for Vuetify theme tokens later). */
  --ll-green: #29bf12;
  --ll-danger: #dc372f;
  margin: 4px 26px 24px;
  padding-top: 4px;
}
.disb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}
.disb-title {
  font-size: 14px;
  font-weight: 700;
  color: #424242;
}
.disb-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.disb-state {
  padding: 24px 0;
  text-align: center;
}
.disb-state--error {
  font-size: 13px;
  color: var(--ll-danger);
}

.disb-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.disb-table thead tr {
  border-bottom: 2px solid #e5e5e5;
}
.disb-th {
  padding: 6px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #747474;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.text-left {
  text-align: left;
}
.text-right {
  text-align: right;
}
.disb-table tbody td {
  padding: 10px 8px;
  vertical-align: middle;
}
.disb-row {
  border-bottom: 1px solid #f5f5f5;
}
.disb-row--firm {
  background: #fafffe;
}
/* Submitted rows are tinted for the full row (prototype: row background #fafbfc). */
.disb-row--submitted,
.disb-row--submitted.disb-row--firm {
  background: #fafbfc;
}
.disb-col-check {
  width: 36px;
  text-align: center;
  padding: 10px 8px;
}
.disb-recipient {
  font-weight: 600;
  color: #424242;
}
.disb-kind {
  font-size: 11px;
  color: #8a929c;
}
.disb-type {
  color: #555;
}
.disb-breakdown {
  font-size: 11px;
  color: #8a929c;
  margin-top: 2px;
}
.disb-amount {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.disb-status {
  text-align: right;
  white-space: nowrap;
  width: 150px;
}
.disb-dash {
  font-size: 12px;
  color: #b0b6bd;
}
.disb-msg-row td {
  padding: 0 8px 8px !important;
}
.disb-msg {
  font-size: 11px;
  color: #747474;
}
.disb-msg--error {
  color: var(--ll-danger);
}
.disb-empty {
  text-align: center;
  color: #999;
  padding: 18px 0;
  font-size: 13px;
}

/* Submitted state — green "Submitted" label; hovering (or focusing) the status
   cell swaps it for the orange Undo button (prototype disbShowUndo/disbHideUndo). */
.disb-submitted {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
}
.disb-submitted-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ll-green);
}
.disb-undo-btn {
  display: none;
}
/* Recovery state (error / needs-recovery) — Retry and Undo can both be visible
   at once, unlike the hover-reveal Undo in the submitted state above. */
.disb-recovery-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}
.disb-status:hover .disb-submitted-label,
.disb-status:focus-within .disb-submitted-label {
  display: none;
}
.disb-status:hover .disb-undo-btn,
.disb-status:focus-within .disb-undo-btn {
  display: inline-flex;
}

/* Info dot — cloned from prototype .info-dot */
.info-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid #90a4ae;
  color: #90a4ae;
  font-size: 10px;
  font-weight: 700;
  cursor: help;
  vertical-align: middle;
  margin-left: 4px;
  font-style: normal;
  line-height: 1;
}
.info-dot:hover,
.info-dot:focus {
  border-color: var(--ll-green);
  color: var(--ll-green);
  outline: none;
}

.disb-btn,
.disb-btn-sm {
  text-transform: none;
  letter-spacing: 0;
}
</style>
